import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Mount-anywhere bridge that auto-redeems a stashed referral code AFTER
 * the user finishes Google auth. Lives outside AuthContext / AuthCallback
 * (those are protected per project rules) so it can be added/removed
 * without touching auth-critical code.
 *
 * Flow:
 *   1. Visitor lands on `/?ref=ABCD1234` → Landing.jsx writes
 *      `oak_referral_code=ABCD1234` to localStorage.
 *   2. Visitor signs in via Google → AuthCallback completes the exchange
 *      and `useAuth().user` becomes non-null.
 *   3. This bridge fires once: POST /api/earn/redeem-referral with the code.
 *   4. Code is removed from localStorage regardless of result so we never
 *      retry forever (backend has its own 24h-since-signup guard).
 */
export default function ReferralRedeemBridge() {
  const { user, initialSessionFetched } = useAuth();

  useEffect(() => {
    if (!initialSessionFetched || !user) return;
    let code = null;
    try { code = localStorage.getItem("oak_ref"); } catch {}
    if (!code) return;
    // Always clear first so a hard-refresh during the redeem call doesn't
    // double-fire. Backend is idempotent anyway via referrals.invitee_user_id.
    try { localStorage.removeItem("oak_ref"); } catch {}
    api.post("/earn/redeem-referral", { code })
      .then(({ data }) => {
        if (data?.status === "ok" && (data.invitee_granted ?? 0) > 0) {
          toast.success(`Welcome bonus: +${data.invitee_granted} credits from your referral!`);
        }
      })
      .catch(() => {
        // Silent fail — invalid/expired codes shouldn't surface as errors
        // to a user who just signed up. Backend logs the attempt.
      });
  }, [initialSessionFetched, user]);

  return null;
}
